/**
 * General step 1 of a container: its master config — never chosen. Every
 * container of a campaign group follows that group's master config, so the
 * field shows it (Open edits it). Until the campaign is picked there is no
 * group yet; a follower whose campaign group has another master config is
 * offered that one.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Crown } from 'lucide-react';
import type { DropGenMasterSummary } from '@trafficpulse/shared';

import { Button, HintDot } from '@/components/ui';

import { fetchContainerDetail } from '../../../../hooks/fetch-container-detail';
import { middleTruncate } from '../../../campaign/campaign-scope';
import { FIELD_ICON, FIELD_LABEL } from '../../general/fields/field-chrome';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { useSectionSources } from '../section-sources';
import { masterNote } from './master-note';
import { type EditorGroup, useEditorGroup } from './use-editor-group';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
}

const HINT =
  "Picked for you: every container of a campaign group follows the group's master config — gateways, texts, options, verdicts and hours. Schedule, slots and lists stay the container's own.";

/** Follow the group's master config: its settings re-seed every inherited section. */
function useFollow(editor: ContainerEditor, group: EditorGroup) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const follow = async (ref: DropGenMasterSummary) => {
    setBusy(true);
    try {
      const detail = await fetchContainerDetail(queryClient, ref.id);
      const members = group.members.map((m) => m.wiserId);
      const current = editor.draft.campaignWiserId;
      const keep = current !== null && members.includes(current);
      editor.setMaster(ref, detail.effective ?? null, keep ? current : members.length === 1 ? members[0] : null);
    } finally {
      setBusy(false);
    }
  };
  return { follow, busy };
}

const Tile: FC<{ master: DropGenMasterSummary | null; onOpen: () => void }> = ({ master, onOpen }) => (
  <span className="flex h-10 min-w-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-3 text-sm">
    {master ? (
      <>
        <Crown className="h-3.5 w-3.5 shrink-0" style={{ color: master.color }} />
        <span className="truncate font-medium">{master.name}</span>
        <button type="button" onClick={onOpen} className="ml-auto shrink-0 text-xs font-medium text-primary hover:underline">
          Open
        </button>
      </>
    ) : (
      <span className="truncate text-muted-foreground">Its campaign group’s — set once the campaign is picked</span>
    )}
  </span>
);

export const MasterField: FC<Props> = ({ editor, countryKey }) => {
  const sources = useSectionSources();
  const group = useEditorGroup(editor, countryKey);
  const { follow, busy } = useFollow(editor, group);
  const followed = editor.draft.masterId === null ? null : sources.masterRef;
  const note = masterNote(followed, { ...group, label: middleTruncate(group.label, 40) });
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className={FIELD_LABEL}>
        <Crown className={FIELD_ICON} /> Master config
        <HintDot label={HINT} />
      </span>
      <Tile master={followed} onOpen={() => followed && sources.openMaster(followed.id)} />
      <span className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] ${note.tone === 'warning' ? 'text-warning' : 'text-muted-foreground'}`} title={group.fullLabel}>
        {note.tone === 'warning' ? <AlertTriangle className="h-3 w-3 shrink-0" /> : null}
        <span className="min-w-0 truncate">{note.text}</span>
        {note.action ? (
          <Button size="sm" variant="outline" className="h-6 shrink-0 px-2 text-xs" disabled={busy} onClick={() => follow(note.action!.master)}>
            {note.action.label}
          </Button>
        ) : null}
      </span>
    </div>
  );
};
